import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../../constants/appConstants';
import { TaskEditData } from '../../hooks/useCurrentTasks';
import { useIntranetData } from '../../hooks/useIntranetData';
import TaskEditModal from './TaskEditModal';
import './ReviewTasksPanel.css';

interface ProjectOption {
  id: string;
  name: string;
  accountId: string;
}

interface ReviewTask {
  id: string;
  accountName: string;
  billingCode: string;
  description: string;
  createdBy: string;
  taskType: string;
  taskTypeCode: string;
  codeId: string;
  dateClosed: string;
  hours: number;
  hasWarning: boolean;
  typeClass: string;
}

interface ReviewTasksData {
  tasks: ReviewTask[];
  totalCount: number;
  totalHours: number;
}

interface ReviewTasksPanelProps {
  employeeId: string | null;
  onCountChange?: (count: number) => void;
}

const ReviewTasksPanel: React.FC<ReviewTasksPanelProps> = ({ employeeId, onCountChange }) => {
  const [data, setData] = useState<ReviewTasksData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [markingTaskId, setMarkingTaskId] = useState<string | null>(null);
  const [markedTasks, setMarkedTasks] = useState<Set<string>>(new Set());
  
  // Intranet data for edit modal
  const { accounts } = useIntranetData();
  const [allProjects, setAllProjects] = useState<ProjectOption[]>([]);

  // Fetch all projects for the edit modal — try DB for instant display, then refresh from intranet
  useEffect(() => {
    const fetchAllProjects = async () => {
      try {
        // Try local DB for instant display
        const dbResponse = await fetch(`${API_URL}/projects`, { credentials: 'include' });
        if (dbResponse.ok) {
          const dbData = await dbResponse.json();
          if (dbData && dbData.length > 0) {
            setAllProjects(dbData.map((p: any) => ({ id: p.id, name: p.name || p.formattedName, accountId: p.accountId })));
          }
        }
        
        // Always fetch fresh from intranet
        const intranetResponse = await fetch(`${API_URL}/intranet/projects/all-with-accounts`, { credentials: 'include' });
        if (intranetResponse.ok) {
          const intranetData = await intranetResponse.json();
          if (intranetData && intranetData.length > 0) {
            setAllProjects(intranetData.map((p: any) => ({ id: p.id, name: p.name || p.formattedName, accountId: p.accountId })));
          }
        }
      } catch (err) {
        console.error('Error fetching projects:', err);
      }
    };
    fetchAllProjects();
  }, []);
  
  // Edit modal state
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [taskEditData, setTaskEditData] = useState<TaskEditData | null>(null);
  const [isLoadingTaskEdit, setIsLoadingTaskEdit] = useState(false);
  const [isSavingTask, setIsSavingTask] = useState(false);

  const fetchReviewTasks = useCallback(async () => {
    if (!employeeId) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(
        `${API_URL}/intranet/tasks/${employeeId}/review`,
        { credentials: 'include' }
      );
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Session expired. Please log in again.');
        }
        throw new Error('Failed to fetch review tasks');
      }
      
      const result = await response.json();
      setData(result);
      // Notify parent of count change
      onCountChange?.(result.totalCount || 0);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch review tasks');
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  // Fetch on mount and when employeeId changes
  useEffect(() => {
    fetchReviewTasks();
  }, [fetchReviewTasks]);

  // Mark a task as reviewed (Good)
  const handleMarkGood = async (task: ReviewTask) => {
    if (!employeeId || markingTaskId) return;
    
    setMarkingTaskId(task.id);
    
    try {
      const response = await fetch(`${API_URL}/intranet/tasks/mark-good`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          employeeId,
          taskId: task.id,
          taskType: task.taskTypeCode,
          codeId: task.codeId
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to mark task as reviewed');
      }
      
      // Add to marked set for animation
      setMarkedTasks(prev => new Set(prev).add(task.id));
      
      // Remove from list after animation
      setTimeout(() => {
        setData(prev => {
          if (!prev) return prev;
          const updatedTasks = prev.tasks.filter(t => t.id !== task.id);
          const newCount = prev.totalCount - 1;
          // Notify parent of count change
          onCountChange?.(newCount);
          return {
            ...prev,
            tasks: updatedTasks,
            totalCount: newCount,
            totalHours: prev.totalHours - task.hours
          };
        });
        setMarkedTasks(prev => {
          const newSet = new Set(prev);
          newSet.delete(task.id);
          return newSet;
        });
      }, 300);
    } catch (err: any) {
      console.error('Error marking task as good:', err);
    } finally {
      setMarkingTaskId(null);
    }
  };

  // Fetch task data for editing
  const getTaskForEdit = async (taskId: string): Promise<TaskEditData | null> => {
    try {
      const response = await fetch(`${API_URL}/intranet/tasks/${taskId}/edit`, {
        credentials: 'include'
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch task details');
      }
      
      return await response.json();
    } catch (err) {
      console.error('Error fetching task for edit:', err);
      return null;
    }
  };

  // Update a task
  const updateTask = async (taskData: TaskEditData): Promise<{ success: boolean; message?: string }> => {
    try {
      const response = await fetch(`${API_URL}/intranet/tasks/update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(taskData)
      });
      
      if (!response.ok) {
        throw new Error('Failed to update task');
      }
      
      return await response.json();
    } catch (err: any) {
      console.error('Error updating task:', err);
      return { success: false, message: err.message };
    }
  };

  // Open the edit modal
  const handleEdit = async (task: ReviewTask) => {
    setIsEditModalOpen(true);
    setIsLoadingTaskEdit(true);
    setTaskEditData(null);
    
    try {
      const data = await getTaskForEdit(task.id);
      setTaskEditData(data);
    } catch (err) {
      console.error('Failed to load task for editing:', err);
    } finally {
      setIsLoadingTaskEdit(false);
    }
  };

  // Close the edit modal
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setTaskEditData(null);
  };

  // Save the edited task
  const handleSaveTask = async (taskData: TaskEditData) => {
    setIsSavingTask(true);
    
    try {
      const result = await updateTask(taskData);
      if (result?.success) {
        handleCloseEditModal();
        // Refresh the task list to reflect changes
        fetchReviewTasks();
      } else {
        throw new Error(result?.message || 'Failed to update task');
      }
    } finally {
      setIsSavingTask(false);
    }
  };

  // Format hours display
  const formatHours = (hours: number): string => {
    if (hours === 0) return '.00 hrs';
    return `${hours.toFixed(2)} hrs`;
  };

  // Get type class name
  const getTypeClass = (task: ReviewTask): string => {
    const type = task.taskType.toLowerCase();
    if (type.includes('project')) return 'project';
    if (type.includes('update')) return 'update';
    if (type.includes('support')) return 'support';
    if (type.includes('internal')) return 'internal';
    if (type.includes('fix')) return 'fix';
    if (type.includes('fallout')) return 'fallout';
    return task.typeClass || '';
  };

  if (!employeeId) {
    return (
      <div className="review-tasks-panel">
        <div className="review-empty">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="12" y1="16" x2="12" y2="12"></line>
            <line x1="12" y1="8" x2="12.01" y2="8"></line>
          </svg>
          <div className="review-empty-text">Please log in to view review tasks</div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="review-tasks-panel">
        <div className="review-panel-header">
          <h3>
            Review Tasks
            <span className="review-count-badge">...</span>
          </h3>
        </div>
        <div className="review-loading">
          <div className="loading-spinner"></div>
          <span>Loading review tasks...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="review-tasks-panel">
        <div className="review-panel-header">
          <h3>Review Tasks</h3>
          <button 
            className="review-refresh-btn" 
            onClick={fetchReviewTasks}
            title="Retry"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M23 4v6h-6"></path>
              <path d="M1 20v-6h6"></path>
              <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
            </svg>
          </button>
        </div>
        <div className="review-error">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"></circle>
            <line x1="15" y1="9" x2="9" y2="15"></line>
            <line x1="9" y1="9" x2="15" y2="15"></line>
          </svg>
          <div>{error}</div>
        </div>
      </div>
    );
  }

  const tasks = data?.tasks || [];
  const totalCount = data?.totalCount || 0;
  const totalHours = data?.totalHours || 0;

  return (
    <div className="review-tasks-panel">
      {/* Task Edit Modal */}
      <TaskEditModal
        task={taskEditData}
        isOpen={isEditModalOpen}
        isLoading={isLoadingTaskEdit}
        isSaving={isSavingTask}
        accounts={accounts}
        projects={allProjects}
        onClose={handleCloseEditModal}
        onSave={handleSaveTask}
      />

      <div className="review-panel-header">
        <h3>
          Review Tasks
          {totalCount > 0 && (
            <span className="review-count-badge">{totalCount}</span>
          )}
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span className="review-total-hours">{totalHours.toFixed(2)} hrs total</span>
          <button 
            className="review-refresh-btn" 
            onClick={fetchReviewTasks}
            disabled={loading}
            title="Refresh"
          >
            {loading ? (
              <div className="review-btn-spinner"></div>
            ) : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M23 4v6h-6"></path>
                <path d="M1 20v-6h6"></path>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path>
              </svg>
            )}
          </button>
        </div>
      </div>

      <div className="review-info-banner">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="16" x2="12" y2="12"></line>
          <line x1="12" y1="8" x2="12.01" y2="8"></line>
        </svg>
        <span>Review each task and click "Good" if it's correct, or "Edit" to make changes.</span>
      </div>

      {tasks.length === 0 ? (
        <div className="review-empty">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
            <polyline points="22 4 12 14.01 9 11.01"></polyline>
          </svg>
          <div className="review-empty-text">
            All tasks reviewed!<br />
            <span style={{ fontSize: '12px', opacity: 0.7 }}>No tasks pending review</span>
          </div>
        </div>
      ) : (
        <div className="review-tasks-list">
          {tasks.map(task => {
            const typeClass = getTypeClass(task);
            const isMarking = markingTaskId === task.id;
            const isMarked = markedTasks.has(task.id);
            
            return (
              <div 
                key={task.id} 
                className={`review-task-item type-${typeClass} ${isMarking ? 'marking-good' : ''} ${isMarked ? 'marked-good' : ''}`}
              >
                <div className="review-task-info">
                  <div className="review-task-account">{task.accountName}</div>
                  <div className="review-task-description">{task.description}</div>
                  <div className="review-task-meta">
                    <span className={`review-task-type ${typeClass}`}>{task.taskType}</span>
                    {task.billingCode && (
                      <span className="review-task-billing">{task.billingCode}</span>
                    )}
                    <span className="review-task-date">{task.dateClosed}</span>
                  </div>
                </div>
                
                {task.hasWarning && (
                  <div className="review-task-warning" title="Task is over 3 hours - consider splitting">
                    <svg viewBox="0 0 24 24">
                      <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  </div>
                )}
                
                <div className={`review-task-hours ${task.hours > 3 ? 'warning' : ''}`}>
                  {formatHours(task.hours)}
                </div>
                
                <div className="review-task-actions">
                  <button 
                    className="review-btn-edit"
                    onClick={() => handleEdit(task)}
                    disabled={isMarking}
                    title="Edit task on intranet"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                    Edit
                  </button>
                  <button 
                    className="review-btn-good"
                    onClick={() => handleMarkGood(task)}
                    disabled={isMarking || isMarked}
                    title="Mark task as reviewed"
                  >
                    {isMarking ? (
                      <div className="review-btn-spinner"></div>
                    ) : (
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                    )}
                    Good
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReviewTasksPanel;

